import { useEffect, useState, useCallback } from 'react';
import { TIER_TYPES } from '../../constans/parameter';
import { Box, Button, Card, CardActions, CardContent, Divider, Grid, TextField, Typography } from '@mui/material';
import useApiService from '../../component/hooks/useApiService';
import Select from '../../component/ui/select/select';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import { apiUrls } from '../../constans/apiUrls';
import stringFormat from '../../component/common/stringFormat';

const AddUser = ({ data, close, adminPanelAction, openType }) => {
    const { executePost, executeDelete, executePatch, executeGet, executePut } = useApiService();
    const [dataModel, setDataModel] = useState({});
    const [tierTypes] = useState(TIER_TYPES);
    const [viewMode, setViewMode] = useState(false);
    const { showSnackbar } = useAppSnackbar();
    const [admins, setAdmins] = useState([]);
    const [canUseAdminDropdown, setCanUseAdminDropdown] = useState(false);
    const [assignedAdminId, setAssignedAdminId] = useState('');

    const setField = (fields) => {
        setDataModel(oldState => ({ ...oldState, ...fields }));
    };

    const findAdminEmailById = (id) => {
        const numericId = Number(id);
        if (!id || Number.isNaN(numericId)) return '';
        const match = admins.find((admin) => Number(admin.id) === numericId);
        return match?.email || '';
    };

    const handleAdminSelection = (value) => {
        const normalizedValue = value ?? '';
        const adminEmail = findAdminEmailById(value);
        setAssignedAdminId(normalizedValue);
        setField({ admin_id: normalizedValue, admin_email: adminEmail || '' });
    };

    const getCurrentAdminEmail = () => {
        const email = dataModel?.admin_email || findAdminEmailById(dataModel?.admin_id);
        return email || '';
    };

    useEffect(() => {
        checkViewMode();
        if (data) { getDataByGrid(); }
        initAdminDropdown();
        if (data?.id) {
            fetchCurrentAssignedAdmin(data.id);
        }
    }, []);

    const checkViewMode = () => {
        if (openType == "View" || openType == "Delete") {
            setViewMode(true);
        }
    }

    const getDataByGrid = () => {
        if (data) {
            setField(data);
            if (typeof data.admin_id !== 'undefined' && data.admin_id !== null) {
                setAssignedAdminId(data.admin_id);
            }
        }
    }

    // Attempts to enable admin dropdown for super_admins; falls back to numeric input on 403.
    const initAdminDropdown = async () => {
        const role = (sessionStorage.getItem('role') || '').toLowerCase();
        if (role !== 'super_admin') {
            setCanUseAdminDropdown(false);
            return;
        }
        try {
            const res = await executeGet({ url: apiUrls.Admin });
            const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
            setAdmins(items);
            setCanUseAdminDropdown(true);
        } catch (err) {
            if (err?.response?.status === 403) {
                setCanUseAdminDropdown(false);
            } else {
                setCanUseAdminDropdown(false);
            }
        }
    };

    // Reads the active assigned admin for the user, if available.
    const fetchCurrentAssignedAdmin = async (userId) => {
        try {
            const res = await executeGet({ url: stringFormat(apiUrls.UserGetAssignedAdmin, userId) });
            if (res && typeof res.admin_id !== 'undefined') {
                const responseEmail = res?.admin_email || res?.admin?.email || '';
                const derivedEmail = responseEmail || findAdminEmailById(res.admin_id);
                setField({ admin_id: res.admin_id, admin_email: derivedEmail || '' });
                setAssignedAdminId(res.admin_id ?? '');
            } else {
                // No admin returned, clear local state
                setField({ admin_id: '', admin_email: '' });
                setAssignedAdminId('');
            }
        } catch (err) {
            // Keep existing state if fetch fails (e.g., lack of permission)
            setAssignedAdminId((prev) => prev || data?.admin_id || '');
        }
    };

    const requiredValidation = () => {
        const isPasswordFlow = openType === 'PasswordUpdate';

        const requiredFields = isPasswordFlow
            ? [
                { key: 'password', label: "Şifre" },
                { key: 'ConfirmPassword', label: "Şifre Tekrar" },
            ]
            : [
                { key: 'email', label: "Email" },
                { key: 'password', label: "Şifre" },
                { key: 'ConfirmPassword', label: "Şifre Tekrar" },
                { key: 'tier_id', label: "Kullanıcı Kategorisi" },
                { key: 'score', label: "Puan" },
                { key: 'meta_id', label: "Meta ID" }
            ];

        for (let field of requiredFields) {
            if (!dataModel[field.key]) {
                showSnackbar(`${field.label} zorunludur.`, 'error');
                return false;
            }
        }

        if (dataModel.password !== dataModel.ConfirmPassword) {
            showSnackbar("Şifreler eşleşmiyor", 'error');
            return false;
        }
        if (!isPasswordFlow && !isValidEmail(dataModel.email)) {
            showSnackbar('Email formatı geçersiz.', 'error');
            return false;
        }
        if ((dataModel.password || '').length < 6) {
            showSnackbar('Şifre minimum 6 karakterli olmalı.', 'error');
            return false;
        }
        return true;
    };

    const createNewAdmin = async () => {

        if (!requiredValidation()) {
            return;
        }
        const postUserData = { ...dataModel };
        delete postUserData.ConfirmPassword;
        // If admin_id is empty, do not send it in payload
        if (postUserData.admin_id === '' || postUserData.admin_id === null || typeof postUserData.admin_id === 'undefined') {
            delete postUserData.admin_id;
        } else if (/^\d+$/.test(String(postUserData.admin_id))) {
            postUserData.admin_id = Number(postUserData.admin_id);
        }
        if (postUserData.admin_id && !postUserData.admin_email) {
            const derivedEmail = findAdminEmailById(postUserData.admin_id);
            if (derivedEmail) {
                postUserData.admin_email = derivedEmail;
            }
        }

        try {
            const res = await executePost({
                url: apiUrls.User,
                data: postUserData
            });
            if (res) {
                close && close(res);
            }
        } catch (err) {
            const status = err?.response?.status;
            const code = err?.response?.data?.error;
            if (status === 409 || code === 'duplicate_email') {
                showSnackbar('Bu email zaten kayıtlı.', 'error');
            } else {
                showSnackbar('Kullanıcı oluşturulamadı.', 'error');
            }
            console.error('İşlem hata aldı:', err);
        }
    };


    const isValidEmail = (email) => {
        const s = String(email || '').trim();
        return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(s);
    };

    const DeleteUser = () => {
        executeDelete({
            url: stringFormat(apiUrls.UserDelete, dataModel?.id),
            data: dataModel?.id
        }).then(() => {
            close(true);
        })

    }

    const updateAdminDetail = async () => {
        const selectedData = {
            email: dataModel.email,
            score: dataModel.score,
            meta_id: dataModel.meta_id,
            tier_id: dataModel.tier_id,
        };

        const adminIdRaw = dataModel.admin_id;
        const trimmedId = String(adminIdRaw ?? '').trim();
        if (trimmedId !== '') {
            const parsedId = /^\d+$/.test(trimmedId) ? Number(trimmedId) : trimmedId;
            selectedData.admin_id = parsedId;
            const adminEmail = dataModel.admin_email || findAdminEmailById(parsedId);
            if (adminEmail) {
                selectedData.admin_email = adminEmail;
            }
        } else {
            // allow clearing admin assignment
            selectedData.admin_id = null;
            selectedData.admin_email = '';
        }
        try {
            await executePatch({
                url: stringFormat(apiUrls.UserUpdate, dataModel?.id),
                data: selectedData
            });
            close(true);
        } catch (err) {
            const status = err?.response?.status;
            const code = err?.response?.data?.error;
            if (status === 409 || code === 'duplicate_email') {
                showSnackbar('Bu email zaten kayıtlı.', 'error');
            } else {
                showSnackbar('Kullanıcı güncellenemedi.', 'error');
            }
            console.error('Güncelleme hata aldı:', err);
        }

    }

    const updateUserPassword = async () => {
        if (!requiredValidation()) return;
        try {
            await executePut({
                url: stringFormat(apiUrls.UserPasswordUpdate, dataModel?.id),
                data: { password: dataModel.password },
            });
            close(true);
        } catch (err) {
            showSnackbar('Şifre güncellenemedi. Hata: ' + (err?.message || 'Bilinmeyen hata'), 'error');
            close(false);
        }
    };

    const filteredActionList = () => {
        switch (openType) {
            case "New":
                return actionList.filter(action => action.name === 'Save' || /* action.name === 'Documents' || */ action.name === 'Cancel');
            case "View":
                return actionList.filter(action => /* action.name === 'Documents' || */ action.name === 'Cancel');
            case "Update":
                return actionList.filter(action => action.name === 'Cancel' || action.name === adminPanelAction?.name);
            case "PasswordUpdate":
                return actionList.filter(action => action.name === 'Cancel' || action.name === adminPanelAction?.name);
            case "Delete":
                return actionList.filter(action => action.name === 'Cancel' || action.name === adminPanelAction?.name);
            default:
                return actionList.filter(action => action.name === 'Save' ||  /* action.name === 'Documents' || */  action.name === 'Cancel');
        }
    }
    const actionList = [
        {
            name: 'Save',
            label: 'Kaydet',
            order: 3
        },
        {
            name: 'Update',
            label: "Güncelle",
            order: 3
        },

        { name: 'Cancel', variant: 'text', label: "Vazgeç", order: 0 },
        ...getadminPanelAction()

    ]

    function getadminPanelAction() {
        return adminPanelAction ? [
            {
                name: adminPanelAction.name,
                label: adminPanelAction.label,
                order: 3
            }
        ] : [];
    }
    const onActionClick = useCallback((action) => {
        if (action.name === 'Save') {
            createNewAdmin();
        }
        else if (action.name === 'DeleteUser') {
            DeleteUser();
        }
        else if (action.name === 'ChangeRequest') {
            updateAdminDetail();
        }
        else if (action.name === 'UserChangePassword') {
            updateUserPassword();
        }
        else if (action.name == 'Cancel') {
            close && close();
        }
    }, [dataModel]);

    const isPasswordFlow = openType === 'PasswordUpdate';

    return (
        <Card sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
            <Box sx={{ flex: 1, overflow: 'auto', }}>
                <CardContent
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(1fr, 1fr))',
                        gap: 2,
                    }}
                >
                    {isPasswordFlow ? (
                        <>
                            <TextField
                                name="email"
                                label="Email"
                                fullWidth
                                required
                                type="email"
                                slotProps={{
                                    input: { readOnly: true },
                                }}
                                value={dataModel?.email || ''}
                            />
                            <TextField
                                required
                                name="password"
                                label="Şifre"
                                type="password"
                                fullWidth
                                value={dataModel?.password || ''}
                                onChange={(e) => setField({ password: e.target.value })}
                            />

                            <TextField
                                required
                                name="ConfirmPassword"
                                label="Şifre Tekrar"
                                type="password"
                                fullWidth
                                value={dataModel?.ConfirmPassword || ''}
                                onChange={(e) => setField({ ConfirmPassword: e.target.value })}
                            />
                        </>
                    ) : (
                        <>
                    {(openType === 'Update' || openType === 'View') && (
                        <Box sx={{ gridColumn: '1 / -1', mb: 1 }}>
                            {/* Section title (Turkish UI text) */}
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Mevcut Admin</Typography>
                            <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                                {openType === 'View' ? (
                                    <TextField
                                        fullWidth
                                        label="Atanan Admin Email"
                                        value={getCurrentAdminEmail() || '—'}
                                        slotProps={{ input: { readOnly: true } }}
                                    />
                                ) : (
                                    <Grid container spacing={2} alignItems="center">
                                        {canUseAdminDropdown ? (
                                            <Grid item xs={12} sm={6} md={6}>
                                                <Select
                                                    name="assigned_admin"
                                                    label="Atanan Admin"
                                                    datasource={admins}
                                                    columns={["email"]}
                                                    valuePath="id"
                                                    value={assignedAdminId ?? ''}
                                                    onChange={(value) => handleAdminSelection(value)}
                                                    displayEmpty
                                                    placeholder="Admin seçiniz"
                                                    shrinkLabel
                                                    sx={{ minWidth: 300 }}
                                                />
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12} sm={6} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Atanan Admin ID"
                                                    placeholder={assignedAdminId ? '' : 'Admin atanmadı'}
                                                    value={assignedAdminId ?? ''}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        if (/^\d*$/.test(v)) handleAdminSelection(v);
                                                    }}
                                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </Box>
                            <Divider sx={{ mt: 2 }} />
                        </Box>
                    )}
                    <TextField
                        name="email"
                        label="Email"
                        fullWidth
                        required
                        type="email"
                        slotProps={{
                            input: { readOnly: viewMode },
                        }}
                        onBlur={(e) => {
                            const value = e.target.value.trim();
                            setField({ email: value });
                            if (value && !isValidEmail(value)) {
                                showSnackbar('Email formatı geçersiz.', 'error');
                            }
                        }}
                        value={dataModel?.email}
                        onChange={(e) => setField({ email: e.target.value })}
                    />

                    {/* Kullanıcı Kategorisi */}
                    <Select
                        name="tier_id"
                        label="Kullanıcı Kategorisi"
                        datasource={tierTypes}
                        columns={["ValueText1"]}
                        required
                        valuePath="Code"
                        readOnly={viewMode}
                        value={dataModel?.tier_id}
                        onChange={(value) => setField({ tier_id: value })}
                    />

                    {/* Meta ID */}
                    <TextField
                        name="meta_id"
                        label="Meta ID"
                        placeholder="Örn: 0200"
                        fullWidth
                        required
                        slotProps={{ input: { readOnly: viewMode } }}
                        value={dataModel?.meta_id || ''}
                        onChange={(e) => setField({ meta_id: e.target.value })}
                    />

                    {/* Puan */}
                    <TextField
                        name="Score"
                        label="Puan"
                        fullWidth
                        type="number"
                        required
                        slotProps={{
                            input: { readOnly: viewMode },
                        }}
                        inputProps={{
                            min: 0,
                            step: 1,
                            pattern: "[0-9]*"
                        }}
                        value={dataModel?.score ?? ""}
                        onKeyDown={(e) => {
                            if (["-", "+", ",", "."].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                                setField({ score: Number(val) });
                            }
                        }}
                    />

                    {/* Şifre alanları (Create) */}
                    {openType === "Create" && (
                        <TextField
                            required
                            name="password"
                            label="Şifre"
                            type="password"
                            fullWidth
                            slotProps={{
                                input: { readOnly: viewMode },
                            }}
                            value={dataModel?.password || ''}
                            onChange={(e) => setField({ password: e.target.value })}
                        />
                    )}

                    {openType === "Create" && (
                        <TextField
                            required
                            name="ConfirmPassword"
                            label="Şifre Tekrar"
                            type="password"
                            fullWidth
                            value={dataModel?.ConfirmPassword || ''}
                            onChange={(e) => setField({ ConfirmPassword: e.target.value })}
                        />
                    )}

                    {/* Optional Admin ID on Create form */}
                    {openType === 'Create' && (
                        canUseAdminDropdown ? (
                            <Select
                                name="admin_id"
                                label="Admin"
                                datasource={admins}
                                columns={["email"]}
                                valuePath="id"
                                readOnly={viewMode}
                                value={dataModel?.admin_id ?? ''}
                                onChange={(value) => handleAdminSelection(value)}
                                displayEmpty
                                placeholder="Admin seçiniz"
                                shrinkLabel
                            />
                        ) : (
                            <TextField
                                name="admin_id"
                                label="Admin ID (optional)"
                                fullWidth
                                value={dataModel?.admin_id ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (/^\d*$/.test(v)) handleAdminSelection(v);
                                }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            />
                        )
                    )}
                        </>
                    )}
                </CardContent>

            </Box>
            <CardActions
                sx={{
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #e0e0e0',
                    p: 2,
                    backgroundColor: '#fff',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                }}
            >

                {filteredActionList()
                    .sort((a, b) => a.order - b.order)
                    .map((action) => (
                        <Button
                            key={action.name}
                            variant={action.variant || 'contained'}
                            onClick={() => onActionClick(action)}
                            sx={{

                                ...(action.variant && {
                                    textTransform: 'none',
                                    color: '#1976d2',
                                    border: 'none',
                                    fontWeight: '500',
                                    '&:hover': {
                                        backgroundColor: '#e3f2fd'
                                    }
                                }),

                                ...(!action.variant && {
                                    textTransform: 'none',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    borderRadius: '10px',
                                    fontWeight: 'bold'
                                })
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
            </CardActions>
        </Card>

    );
};

export default AddUser;
